"use client"

import { useState } from "react"
import { motion } from "framer-motion"

interface AnimatedDiceIconProps {
  className?: string
  size?: number
  dotCount?: number
}

export function AnimatedDiceIcon({ className = "", size = 24, dotCount }: AnimatedDiceIconProps) {
  const [internalDotCount, setInternalDotCount] = useState(5)

  // Use the provided dotCount if available, otherwise use internal state
  const displayDotCount = dotCount !== undefined ? dotCount : internalDotCount

  // Function to get the next dot count (1-6, cycling through dice patterns)
  const getNextDotCount = (current: number) => {
    return (current % 6) + 1
  }

  // Render dots based on the current displayDotCount
  const renderDots = () => {
    switch (displayDotCount) {
      case 1:
        return (
          // Center dot only
          <motion.path
            d="M12.75 12C12.75 12.4142 12.4142 12.75 12 12.75C11.5858 12.75 11.25 12.4142 11.25 12C11.25 11.5858 11.5858 11.25 12 11.25C12.4142 11.25 12.75 11.5858 12.75 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        )
      case 2:
        return (
          // Top-right and bottom-left dots
          <>
            <motion.path
              d="M16.75 8C16.75 8.41421 16.4142 8.75 16 8.75C15.5858 8.75 15.25 8.41421 15.25 8C15.25 7.58579 15.5858 7.25 16 7.25C16.4142 7.25 16.75 7.58579 16.75 8Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M8.75 16C8.75 16.4142 8.41421 16.75 8 16.75C7.58579 16.75 7.25 16.4142 7.25 16C7.25 15.5858 7.58579 15.25 8 15.25C8.41421 15.25 8.75 15.5858 8.75 16Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </>
        )
      case 3:
        return (
          // Top-right, center, and bottom-left dots
          <>
            <motion.path
              d="M16.75 8C16.75 8.41421 16.4142 8.75 16 8.75C15.5858 8.75 15.25 8.41421 15.25 8C15.25 7.58579 15.5858 7.25 16 7.25C16.4142 7.25 16.75 7.58579 16.75 8Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M12.75 12C12.75 12.4142 12.4142 12.75 12 12.75C11.5858 12.75 11.25 12.4142 11.25 12C11.25 11.5858 11.5858 11.25 12 11.25C12.4142 11.25 12.75 11.5858 12.75 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M8.75 16C8.75 16.4142 8.41421 16.75 8 16.75C7.58579 16.75 7.25 16.4142 7.25 16C7.25 15.5858 7.58579 15.25 8 15.25C8.41421 15.25 8.75 15.5858 8.75 16Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </>
        )
      case 4:
        return (
          // Four corners
          <>
            <motion.path
              d="M8.75 8C8.75 8.41421 8.41421 8.75 8 8.75C7.58579 8.75 7.25 8.41421 7.25 8C7.25 7.58579 7.58579 7.25 8 7.25C8.41421 7.25 8.75 7.58579 8.75 8Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M16.75 8C16.75 8.41421 16.4142 8.75 16 8.75C15.5858 8.75 15.25 8.41421 15.25 8C15.25 7.58579 15.5858 7.25 16 7.25C16.4142 7.25 16.75 7.58579 16.75 8Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M8.75 16C8.75 16.4142 8.41421 16.75 8 16.75C7.58579 16.75 7.25 16.4142 7.25 16C7.25 15.5858 7.58579 15.25 8 15.25C8.41421 15.25 8.75 15.5858 8.75 16Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M16.75 16C16.75 16.4142 16.4142 16.75 16 16.75C15.5858 16.75 15.25 16.4142 15.25 16C15.25 15.5858 15.5858 15.25 16 15.25C16.4142 15.25 16.75 15.5858 16.75 16Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </>
        )
      case 5:
        return (
          // Four corners and center
          <>
            <motion.path
              d="M8.75 8C8.75 8.41421 8.41421 8.75 8 8.75C7.58579 8.75 7.25 8.41421 7.25 8C7.25 7.58579 7.58579 7.25 8 7.25C8.41421 7.25 8.75 7.58579 8.75 8Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M16.75 8C16.75 8.41421 16.4142 8.75 16 8.75C15.5858 8.75 15.25 8.41421 15.25 8C15.25 7.58579 15.5858 7.25 16 7.25C16.4142 7.25 16.75 7.58579 16.75 8Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M12.75 12C12.75 12.4142 12.4142 12.75 12 12.75C11.5858 12.75 11.25 12.4142 11.25 12C11.25 11.5858 11.5858 11.25 12 11.25C12.4142 11.25 12.75 11.5858 12.75 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M8.75 16C8.75 16.4142 8.41421 16.75 8 16.75C7.58579 16.75 7.25 16.4142 7.25 16C7.25 15.5858 7.58579 15.25 8 15.25C8.41421 15.25 8.75 15.5858 8.75 16Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M16.75 16C16.75 16.4142 16.4142 16.75 16 16.75C15.5858 16.75 15.25 16.4142 15.25 16C15.25 15.5858 15.5858 15.25 16 15.25C16.4142 15.25 16.75 15.5858 16.75 16Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </>
        )
      case 6:
        return (
          // Six dots (3 on each side)
          <>
            <motion.path
              d="M8.75 8C8.75 8.41421 8.41421 8.75 8 8.75C7.58579 8.75 7.25 8.41421 7.25 8C7.25 7.58579 7.58579 7.25 8 7.25C8.41421 7.25 8.75 7.58579 8.75 8Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M16.75 8C16.75 8.41421 16.4142 8.75 16 8.75C15.5858 8.75 15.25 8.41421 15.25 8C15.25 7.58579 15.5858 7.25 16 7.25C16.4142 7.25 16.75 7.58579 16.75 8Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M8.75 12C8.75 12.4142 8.41421 12.75 8 12.75C7.58579 12.75 7.25 12.4142 7.25 12C7.25 11.5858 7.58579 11.25 8 11.25C8.41421 11.25 8.75 11.5858 8.75 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M16.75 12C16.75 12.4142 16.4142 12.75 16 12.75C15.5858 12.75 15.25 12.4142 15.25 12C15.25 11.5858 15.5858 11.25 16 11.25C16.4142 11.25 16.75 11.5858 16.75 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M8.75 16C8.75 16.4142 8.41421 16.75 8 16.75C7.58579 16.75 7.25 16.4142 7.25 16C7.25 15.5858 7.58579 15.25 8 15.25C8.41421 15.25 8.75 15.5858 8.75 16Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <motion.path
              d="M16.75 16C16.75 16.4142 16.4142 16.75 16 16.75C15.5858 16.75 15.25 16.4142 15.25 16C15.25 15.5858 15.5858 15.25 16 15.25C16.4142 15.25 16.75 15.5858 16.75 16Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </>
        )
      default:
        return null
    }
  }

  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      whileHover={{
        rotate: 360,
        transition: { duration: 0.6, ease: "easeInOut" },
      }}
      onHoverStart={() => {
        if (dotCount === undefined) {
          setInternalDotCount(getNextDotCount(internalDotCount))
        }
      }}
    >
      <motion.path
        d="M3.75 5.75C3.75 4.64543 4.64543 3.75 5.75 3.75H18.25C19.3546 3.75 20.25 4.64543 20.25 5.75V18.25C20.25 19.3546 19.3546 20.25 18.25 20.25H5.75C4.64543 20.25 3.75 19.3546 3.75 18.25V5.75Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {renderDots()}
    </motion.svg>
  )
}
